// Approved summaries of the excerpts configured in regulatoryTopics.js.
// Keep wording and source references aligned with that registry.
const regulatoryTopicOverviews = {
  "sow-sof": {
  "title": "Source of wealth and source of funds requirements",
  "sections": [
    {
      "title": "Establishing the source of wealth and funds",
      "points": [
        "The AML Implementing Resolution requires reasonable measures to identify the source of funds and wealth of the customer and beneficial owner as part of enhanced due diligence.",
        "The applicable AML Guidelines provide for more detailed enquiry into these sources and an assessment of the reasonableness of the information obtained.",
        "The scope and depth of enquiries should reflect the identified risks and the circumstances of the business relationship."
      ]
    },
    {
      "title": "Supporting information and evidence",
      "points": [
        "The applicable AML Guidelines state that enhanced due diligence should involve additional evidence and supporting documentation for high-risk customers.",
        "Relevant information includes source of funds and wealth, occupation or business activity, financial statements and banking references.",
        "Information on business operations, expected currency volumes, sales, major customers and suppliers, and changes in customer activity may support the assessment."
      ]
    },
    {
      "title": "Politically exposed persons",
      "points": [
        "The AML Implementing Resolution requires reasonable measures to identify the source of funds and wealth of customers and beneficial owners identified as foreign PEPs.",
        "These measures also apply to domestic PEPs and persons entrusted with prominent functions in international organisations where a high-risk business relationship exists.",
        "The applicable AML Guidelines provide for evaluating the legitimacy of a PEP’s sources, including reasonable investigation of the individual’s professional and financial background."
      ]
    },
    {
      "title": "Reasonableness and consistency",
      "points": [
        "Information obtained through enhanced due diligence should be evaluated for inconsistencies and potentially unusual or suspicious circumstances.",
        "The assessment should consider whether the customer’s business and transactions are consistent with the customer’s or beneficial owner’s professional background and employment history. Reliable independent information and a CV may assist those enquiries.",
        "Transaction complexity, other business interests and consistency with the counterparty’s activities are also relevant considerations under the applicable AML Guidelines."
      ]
    },
    {
      "title": "Ongoing scrutiny and review",
      "points": [
        "The AML Implementing Resolution requires scrutiny of transactions throughout the relationship for consistency with the customer’s activities and risk, including the source of funds where necessary.",
        "The applicable AML Guidelines provide for procedures specifying when a customer’s source of wealth or source of funds should be verified during CDD reviews.",
        "Review procedures should define the information to be examined and updated according to the customer’s risk category."
      ]
    },
    {
      "title": "Proportionate enquiries",
      "points": [
        "Relationships involving high-risk countries require enhanced measures commensurate with the risks, including more detailed enquiry into the source of funds.",
        "The applicable AML Guidelines permit fewer and less detailed source-of-funds enquiries within simplified due diligence only where the customer has been assessed as low risk and there is no suspicion of money laundering or terrorist financing.",
        "The extent of enquiries should therefore reflect the applicable due-diligence conditions rather than a uniform requirement for every customer."
      ]
    }
  ]
},
  "periodic-review": {
    "title": "Periodic and event-driven review of customer files",
    "sections": [
      {
        "title": "Keeping customer files current",
        "points": [
          "CDD documents, data and information must remain up to date and relevant throughout the business relationship, with particular attention to high-risk customers.",
          "Reviews should cover the customer and relevant beneficial-owner information and confirm whether the existing risk classification remains appropriate.",
          "The applicable AML Guidelines provide for internal policies, procedures and controls governing both periodic and event-driven reviews."
        ]
      },
      {
        "title": "Risk-based review intervals",
        "points": [
          "The organisation should establish a maximum interval between CDD reviews and updates for each customer risk category, proportionate to the risks involved.",
          "Higher-risk customers require more frequent updates. Longer intervals for low-risk customers remain subject to the conditions for simplified due diligence, including the absence of suspicion.",
          "The selected regulatory provisions do not prescribe a universal annual or three-year client-file review cycle. The review schedule should be established in the organisation’s documented risk-based procedures."
        ]
      },
      {
        "title": "Events requiring an earlier review",
        "points": [
          "A scheduled review date does not replace the need to review a file when relevant information or circumstances change.",
          "Triggers include expired identification documents, material changes in ownership or legal structure, changes in relevant customer data, legal proceedings, materially adverse information and unusual or suspicious activity.",
          "Suspicion or doubts concerning the accuracy or adequacy of previously obtained identification data require CDD measures. Changes in legislation or internal procedures may also require file reviews."
        ]
      },
      {
        "title": "Scope and depth of the review",
        "points": [
          "The organisation should define which documents, data and information must be examined and updated for each risk category.",
          "Review procedures should address when source of wealth or funds requires verification, when further enquiries are appropriate, and how much transactional history should be examined.",
          "Checklists and procedural guidance should support consistent reviews whose scope is proportionate to the customer’s risk."
        ]
      },
      {
        "title": "Responsibilities and decisions",
        "points": [
          "Responsibilities should be assigned for carrying out reviews, escalating concerns and approving or rejecting the review of a business relationship.",
          "Escalation arrangements should address changes in risk classification, potential suspension or termination of relationships, and investigation of unusual or suspicious activity.",
          "Review arrangements should provide for appropriate senior-management involvement in relation to PEPs and other high-risk customers."
        ]
      },
      {
        "title": "Remediation, quality and records",
        "points": [
          "Customer-file deficiencies identified during a review should be addressed through appropriate CDD remediation measures.",
          "The organisation should assign responsibility for auditing the quality of reviews and updates.",
          "Records of reviews and updates should be maintained in accordance with the applicable record-keeping requirements."
        ]
      }
    ]
  },
  "risk-factors": {
    "title": "Identification of financial-crime risk factors",
    "sections": [
      {
        "title": "Scope and interaction of risk factors",
        "points": [
          "The organisation must consider relevant customer, geographic, product, service, transaction and delivery-channel risks when assessing its exposure.",
          "Risk factors may operate in combination. Their interaction must inform the assessment of the overall level of risk and the corresponding mitigation measures.",
          "The applicable AML Guidelines distinguish business-wide exposure from the assessment of an individual customer or business relationship."
        ]
      },
      {
        "title": "Customer characteristics",
        "points": [
          "The assessment should consider customer activities, residence, relationship characteristics, ownership and control structures, and the transparency of beneficial ownership.",
          "Complex structures, connections to higher-risk persons or sectors, and intermediary arrangements may affect the organisation’s exposure.",
          "The AML Implementing Resolution identifies high-risk customer characteristics, including high-risk country connections, complex or unjustified transactions, large cash transactions and transactions with unknown third parties."
        ]
      },
      {
        "title": "Geographic exposure",
        "points": [
          "The assessment should consider the jurisdictions in which the organisation operates and those in which customers reside or conduct their activities.",
          "Relevant considerations include the effectiveness of AML/CFT supervision, applicable sanctions, corruption and criminal activity, and transparency.",
          "Geographic exposure should be considered together with the customer’s other inherent risk factors."
        ]
      },
      {
        "title": "Products, services and transactions",
        "points": [
          "The organisation should assess the vulnerability of its products and services to financial-crime abuse, including their association with established typologies.",
          "Relevant factors include complexity, transparency, ease of transferring ownership or control, and transaction size or value.",
          "Particular attention should be given to arrangements favouring anonymity, pooled funds, or limited visibility over the parties controlling funds."
        ]
      },
      {
        "title": "Delivery channels and emerging risks",
        "points": [
          "Delivery-channel assessments should consider remote interactions, third-party introducers and intermediaries, and channels that may favour anonymity.",
          "The organisation must identify and assess risks arising from new products, business practices and technologies before their launch or use, and take appropriate mitigation measures.",
          "The applicable AML Guidelines also identify innovation, cyber-security exposure and distributed networks as considerations in identifying emerging risks."
        ]
      },
      {
        "title": "Information sources and ongoing identification",
        "points": [
          "The organisation should draw on national and topical risk assessments, supervisory information, internal compliance input and relevant official publications.",
          "Identified factors should be reviewed and updated to reflect new threats, changing business activities and developments affecting existing risks.",
          "No list of risk factors is exhaustive; the selection must remain appropriate to the nature and size of the business."
        ]
      }
    ]
  },
  "risk-rating": {
    "title": "Customer risk classification, methodology and review",
    "sections": [
      {
        "title": "Assessment and classification",
        "points": [
          "The organisation must assess, document and continuously update its financial-crime risks, retaining the assessment and related information.",
          "Customer and business-relationship assessments should analyse the identified factors to arrive at an appropriate risk classification.",
          "The process should incorporate national and topical risk assessments, the organisation’s business-wide assessment and relevant internal input."
        ]
      },
      {
        "title": "Proportionate customer rating methodology",
        "points": [
          "The methodology should be appropriate to the nature and size of the business and the risks involved.",
          "The applicable AML Guidelines allow customer classification through generic profiles for suitable customer groups or through more sophisticated models and weighted scorecards.",
          "The chosen methodology and its rationale should be documented, approved by senior management and communicated to the relevant personnel."
        ]
      },
      {
        "title": "Weightings and assessment criteria",
        "points": [
          "The business-wide assessment methodology should describe the weighting of factors, classification of risks and prioritisation of exposures.",
          "The assessment should evaluate the likelihood, timing and impact of identified risks.",
          "The rationale and circumstances permitting manual interventions or exceptions to model-based weightings and classifications should be specified."
        ]
      },
      {
        "title": "Controls and residual risk",
        "points": [
          "Business-wide assessment should distinguish inherent exposure from the residual risk remaining after the application of mitigating controls.",
          "The effectiveness of controls and mitigation measures should be assessed using relevant audit, compliance and management information.",
          "Residual risk should be considered against the organisation’s risk appetite to determine whether additional controls are required."
        ]
      },
      {
        "title": "Consequences for due diligence",
        "points": [
          "The type and intensity of customer due diligence should reflect the assessed customer risk and the circumstances of the relationship.",
          "Customer risk profiles are dynamic. New information, changes in behaviour or doubts about an existing classification may require increased due diligence.",
          "The assessment should support the application of enhanced measures for identified high risks and simplified measures only where the applicable conditions are met."
        ]
      },
      {
        "title": "Documentation, review and validation",
        "points": [
          "Assessment documentation should record the methodology, supporting information, analysis and rationale for classifications.",
          "The organisation should establish periodic reviews and procedures for updating customer classifications and business-wide assessments following relevant internal or external changes.",
          "The business-wide methodology and its output should be evaluated, tested and audited for effectiveness and consistency with the applicable obligations."
        ]
      }
    ]
  },
  "record-keeping": {
    "title": "Record retention, accessibility and control obligations",
    "sections": [
      {
        "title": "Records within scope",
        "points": [
          "The organisation must retain its business risk assessment and related information, customer due diligence information and records of domestic and international transactions.",
          "Records include account files, business correspondence, identification documents, ongoing monitoring information, suspicious transaction reports and the results of analysis undertaken.",
          "The AML Implementing Resolution also covers CCTV, ATM and other recordings within its record-retention requirements."
        ]
      },
      {
        "title": "Federal retention periods",
        "points": [
          "Transaction records must be retained for at least five years from completion of the transaction or termination of the customer relationship, as provided by the AML Implementing Resolution.",
          "CDD, monitoring and related records must be retained for at least five years, calculated from the latest applicable event: termination of the relationship, account closure, completion of an occasional transaction, completion of a supervisory inspection or investigation, or issuance of a final court judgment.",
          "The applicable AML Guidelines recognise that competent authorities may require records to be retained for longer periods."
        ]
      },
      {
        "title": "Customer, company and third-party records",
        "points": [
          "Customer records should include identification and verification evidence, CDD forms and supporting documents, customer risk assessments, correspondence and relevant investigation results.",
          "Where the organisation is involved in company dissolution or liquidation, relevant corporate, ownership and controlling-person records must be retained in accordance with the applicable requirements described in the AML Guidelines.",
          "Reliance on a third party for CDD requires access to the necessary documents upon request and without delay, and assurance that the third party complies with record-keeping requirements."
        ]
      },
      {
        "title": "Monitoring and suspicious transaction records",
        "points": [
          "Records must cover ongoing monitoring, transaction reviews, analysis, investigations and related correspondence.",
          "Transaction approval or rejection decisions should be retained with their supporting analysis and correspondence.",
          "Both internal and external suspicious transaction reports must be retained, together with alerts, investigations, recommendations, reporting decisions, relevant authority requests and FIU feedback."
        ]
      },
      {
        "title": "Organisation and access to records",
        "points": [
          "Records must be organised so that individual transactions can be reconstructed, information analysed and financial transactions traced, including for use as evidence where necessary.",
          "Customer information and related records, files, documents and correspondence must be made available promptly to competent authorities upon request.",
          "Retention arrangements should provide for clear responsibilities for logging, archiving, handling and transferring records, and for the destruction of records when the applicable retention requirements have been satisfied."
        ]
      },
      {
        "title": "Protection and oversight",
        "points": [
          "Record-keeping policies, procedures and controls should be documented, approved by senior management and communicated to the appropriate levels of the organisation.",
          "Physical and cyber-security measures should protect active and archived records against unauthorised access, supported by appropriate contingency and escalation procedures.",
          "Audit and quality-assurance arrangements should assess record-keeping controls, including the monitoring and testing of third-party retention arrangements where used."
        ]
      }
    ]
  },
  "training": {
    "title": "Staff training and awareness obligations",
    "sections": [
      {
        "title": "Governance and responsibility",
        "points": [
          "The Compliance Officer must develop, implement and document ongoing employee training programmes and plans concerning financial crime and methods of combating it.",
          "Ongoing employee training forms part of the internal policies, procedures and controls constituting the first line of defence within the AML/CFT programme.",
          "Senior management is responsible for approving policies and controls relating to staff screening, training and development, and for the effectiveness of the organisation’s compliance culture."
        ]
      },
      {
        "title": "Personnel and organisational coverage",
        "points": [
          "Training should cover relevant AML/CFT laws and regulations together with the organisation’s own risk-mitigation policies and procedures.",
          "Its scope should include customer-facing staff, AML/CFT compliance personnel, senior management and members of the board of directors.",
          "Training measures should apply throughout the organisation and financial group, including foreign branches and majority-owned subsidiaries."
        ]
      },
      {
        "title": "Risk-based content and continuing awareness",
        "points": [
          "Training arrangements should reflect national and topical risk assessments, the organisation’s risk profile, and the nature, size and complexity of its business, products, services and customers.",
          "Staff competency and development needs should inform the content, frequency, structure and delivery of training, together with the allocation of internal and external training resources.",
          "Employees should remain informed of emerging money-laundering and terrorist-financing typologies and new internal and external risks. Regular review of relevant trends and typologies should be incorporated into compliance training.",
          "Terrorist-financing risks include knowingly providing funds for travel for terrorist purposes and for the provision or receipt of terrorist training, as addressed by the AML Decree-Law."
        ]
      },
      {
        "title": "Identification and reporting of suspicion",
        "points": [
          "Training should enable staff to identify unusual or suspicious transactions, including attempted transactions, and appropriately assess relevant indicators.",
          "Programmes should explain internal escalation and reporting procedures and the appropriate extent of investigation before a matter is reported to the Compliance Officer.",
          "Staff should understand that investigations must begin immediately and proceed continuously, with reporting to the FIU without delay once suspicion is established.",
          "Awareness should include the absence of a minimum monetary threshold for suspicious transaction reporting."
        ]
      },
      {
        "title": "Confidentiality and prevention of tipping off",
        "points": [
          "Relevant staff should receive guidance and training on the confidentiality of suspicious transaction reports, their contents and the fact of reporting.",
          "Customer-facing personnel should understand which questions may be put to customers and which information must not be disclosed.",
          "Training should support procedures and access controls protecting reporting information from unauthorised disclosure, including disclosure of an actual or contemplated report or a related investigation."
        ]
      },
      {
        "title": "Effectiveness, documentation and independent review",
        "points": [
          "Training programmes and plans must be documented. Appropriate methods should be used to assess staff competency and the effectiveness of training and development measures.",
          "Independent audit should assess training adequacy, including comprehensiveness, accuracy of materials, the training schedule, attendance tracking and escalation of non-attendance.",
          "Senior management and the Compliance Officer should support appropriately qualified, trained and informed staff and the continuing effectiveness of the AML/CFT programme."
        ]
      }
    ]
  },
  "sars": {
    "title": "Suspicious transaction reporting obligations",
    "sections": [
      {
        "title": "Scope and reporting threshold",
        "paragraphs": [
          "Reporting obligations arise where there are reasonable grounds to suspect that funds constitute criminal proceeds or are connected with money laundering, terrorist financing or proliferation financing."
        ],
        "points": [
          "These obligations extend to completed and attempted transactions, including transactions identified retrospectively.",
          "No minimum monetary threshold applies, and proof of the underlying offence is not required."
        ]
      },
      {
        "title": "Internal escalation and submission",
        "points": [
          "Internal reporting procedures should provide for the prompt escalation of suspicious activity to the designated Compliance Officer, together with relevant information and supporting records.",
          "Investigations into alerts should commence immediately, proceed continuously and be documented.",
          "Where the reporting threshold is met, a report must be submitted to the Financial Intelligence Unit without delay, through the prescribed channel and with all available relevant information."
        ]
      },
      {
        "title": "Confidentiality and customer due diligence",
        "paragraphs": [
          "The report, its contents and the fact of its submission are subject to confidentiality requirements."
        ],
        "points": [
          "Disclosure to the customer or other persons of an actual or contemplated report, or a related investigation, is prohibited.",
          "Where required customer due diligence cannot be completed, the business relationship or transaction must not proceed, and submission of a report must be considered.",
          "Where conducting due diligence would alert the customer to suspected criminal activity, the applicable provisions permit its omission, subject to submission of a report explaining the reasons."
        ]
      },
      {
        "title": "Obligations following submission",
        "points": [
          "Reporting entities must respond to FIU requests and comply with applicable instructions.",
          "Continued relationships should be subject to reassessment of customer risk and appropriate enhanced due diligence and monitoring.",
          "Submission of a report does not, in itself, require termination of the relationship.",
          "Records of alerts, investigations, reporting decisions, supporting evidence and subsequent correspondence should be retained."
        ]
      }
    ]
  },
  "mlro": {
    "title": "Appointment, independence and statutory responsibilities",
    "sections": [
      {
        "title": "Appointment and organisational standing",
        "points": [
          "The organisation must appoint a Compliance Officer at management level with appropriate competence and experience.",
          "The applicable AML Guidelines provide for the relevant Supervisory Authority’s prior consent to the appointment.",
          "The officer’s seniority, qualifications and reporting arrangements should be appropriate to the organisation’s size, complexity and risk profile."
        ]
      },
      {
        "title": "Independence of the compliance function",
        "paragraphs": [
          "The designated compliance function constitutes the second line of defence within the AML/CFT programme."
        ],
        "points": [
          "The Compliance Officer must be able to exercise independent judgement and should have sufficient authority to challenge decisions without undue influence.",
          "Reporting arrangements should preserve the function’s independence.",
          "Actual or potential conflicts of interest involving the officer, management, the governing body or customers should be identified and appropriately addressed."
        ]
      },
      {
        "title": "Transaction monitoring and reporting responsibilities",
        "points": [
          "The Compliance Officer’s responsibilities include monitoring transactions for potential criminal activity and reviewing relevant records.",
          "The officer receives and assesses information concerning suspicious transactions and determines whether a report should be submitted to the FIU.",
          "Decisions not to report must be supported by recorded reasons, with confidentiality maintained.",
          "The officer must cooperate with the FIU and Supervisory Authority and provide requested information and access to relevant records."
        ]
      },
      {
        "title": "Oversight, management reporting and training",
        "points": [
          "The officer reviews internal controls addressing money laundering, terrorist financing and proliferation financing, assesses compliance and proposes necessary improvements.",
          "The role also encompasses contributions to business risk assessment and the effectiveness of policies, controls and customer due diligence.",
          "Periodic reports must be submitted directly to senior management, with copies and management observations provided to the Supervisory Authority upon request.",
          "Responsibilities further include the development, implementation and documentation of ongoing staff training."
        ]
      }
    ]
  },
  "peps": {
    "title": "Identification and enhanced due diligence requirements",
    "sections": [
      {
        "title": "Persons within scope",
        "points": [
          "Politically Exposed Persons include individuals who are, or have been, entrusted with prominent public functions in the UAE or a foreign country, and persons entrusted with prominent functions in international organisations.",
          "The relevant requirements also encompass specified immediate family members and known close associates, including persons connected through joint ownership, close professional or social relationships, or structures established for a PEP’s benefit."
        ]
      },
      {
        "title": "Foreign PEPs",
        "points": [
          "Appropriate risk-management systems must be maintained to determine whether a customer or beneficial owner is a foreign PEP.",
          "Establishing or continuing such a relationship requires senior-management approval.",
          "Reasonable measures must be taken to establish the source of wealth and source of funds of the customer and beneficial owner, and the relationship must be subject to enhanced ongoing monitoring."
        ]
      },
      {
        "title": "Domestic PEPs, international organisations and former PEPs",
        "points": [
          "Adequate measures must be taken to identify domestic PEPs and persons entrusted with prominent functions in international organisations. Where the relationship presents a high risk, the prescribed approval, source-of-wealth, source-of-funds and enhanced-monitoring measures apply.",
          "The treatment of former PEPs should reflect the residual risk associated with their previous functions. Relevant considerations include continuing influence, the seniority of the former position and connections between former and current responsibilities."
        ]
      },
      {
        "title": "Due diligence and reliance on third parties",
        "points": [
          "Enhanced measures should provide for additional verification, reliable supporting evidence and an appropriate understanding of the relationship and transactions. Monitoring and review frequency should reflect the identified risk.",
          "Reliance on a third party does not transfer responsibility for the accuracy of customer due diligence. Required identification information must be obtained immediately, supporting documents must be available without delay, and the third party must satisfy the prescribed regulatory and control conditions."
        ]
      },
      {
        "title": "Life insurance beneficiaries",
        "points": [
          "PEP status must be assessed in relation to the beneficiary or beneficial owner before payment or the exercise of related rights.",
          "Where higher risks are identified, senior management must be informed, enhanced scrutiny applied and submission of a suspicious transaction report considered."
        ]
      }
    ]
  },
  "sanctions": {
    "title": "Targeted financial sanctions obligations",
    "sections": [
      {
        "title": "Scope of application",
        "paragraphs": [
          "Targeted financial sanctions comprise asset-freezing measures and prohibitions on making funds available to designated persons or organisations."
        ],
        "points": [
          "The relevant provisions address the UAE Local Lists and applicable United Nations sanctions lists.",
          "The scope of funds includes financial assets, economic resources and income derived from such assets.",
          "Freezing obligations extend to direct and indirect ownership or control, including arrangements involving persons acting on behalf of listed persons."
        ]
      },
      {
        "title": "Screening requirements",
        "points": [
          "Organisations must register for Executive Office notifications concerning listings, amendments and delistings.",
          "Screening must be undertaken regularly and immediately following notified changes.",
          "Its scope includes customers, prospective customers, beneficial owners, transaction parties and other direct or indirect relationships.",
          "Screening requirements also apply before transactions are conducted and when business relationships are established."
        ]
      },
      {
        "title": "Freezing obligations and prohibitions",
        "points": [
          "Funds falling within the applicable designation must be frozen without delay and without prior notice. The Sanctions and Freezing Decision defines “without delay” by reference to a period of 24 hours following the relevant listing decision.",
          "Funds and related services must not be made available to listed persons except as permitted under the prescribed authorisation arrangements.",
          "Decisions lifting freezing measures must likewise be implemented without delay."
        ]
      },
      {
        "title": "Notification and internal controls",
        "points": [
          "Freezing measures must be notified to the Executive Office within five business days.",
          "Relevant matches, identified funds, attempted transactions and measures taken must be notified immediately to the Supervisory Authority.",
          "Other specified reporting circumstances include unresolved potential matches and relevant information concerning unfrozen funds.",
          "Internal procedures must support effective implementation, prevent advance disclosure of impending measures to customers and facilitate cooperation with the competent authorities."
        ]
      },
      {
        "title": "Related requirements, exceptions and consequences",
        "points": [
          "Simplified due diligence does not displace the obligation to implement targeted financial sanctions fully.",
          "Applicable freezing and transaction prohibitions extend to wire transfers, and VASPs remain subject to the relevant sanctions requirements.",
          "Permitted access to frozen funds, specified payments and additions to frozen accounts remain subject to the prescribed conditions and authorisations.",
          "Non-compliance may give rise to criminal and administrative sanctions."
        ]
      }
    ]
  }
};

export const sarOverviewAdditions = {
  "dnfbp": {
    "title": "Additional provision — DNFBP organisations",
    "text": "The professional-secrecy exemption applies only to the specified legal professionals and independent statutory auditors, and only in the circumstances prescribed by the relevant provisions. It does not constitute a general exemption for DNFBPs."
  },
  "difc": {
    "title": "Additional provision — DIFC organisations",
    "points": [
      "The MLRO’s decision to submit a report must be independent and must not depend on another person’s approval.",
      "The DFSA must be notified immediately following submission to the FIU.",
      "Where no report is submitted, the reasons for that decision must be documented."
    ]
  }
};

export const recordKeepingOverviewAdditions = {
  difc: {
    title: "Additional provisions — DIFC organisations",
    points: [
      "The DFSA AML Rulebook requires the specified CDD, business relationship, transaction and reporting records to be retained for at least six years from the notification or report, termination of the relationship or completion of the transaction, whichever occurs last.",
      "Records and documented business and customer risk assessments must be available to the DFSA immediately upon request. Its guidance ordinarily expects provision within 24 hours, while allowing further time for complex requests or records held outside the DIFC.",
      "Overseas records must remain accessible and available for inspection. Where secrecy or data-protection legislation would restrict access, certified copies must be obtained and kept in a jurisdiction permitting the required access.",
      "Compliance with training and awareness requirements must be demonstrable, including through relevant training records; the guidance identifies training dates, content and participants as appropriate log details."
    ]
  },
  adgm: {
    title: "Additional provisions — ADGM organisations",
    points: [
      "The FSRA AML Rulebook requires the specified records to be retained for at least six years from the notification or report, termination of the relationship, completion of the transaction, conclusion of a related investigation or issuance of a related judgment, whichever occurs last.",
      "The requirements cover CDD, relevant business-partner due diligence, transaction records, internal notifications, SARs/STRs, FIU communications and specified MLRO reporting. Outsourcing CDD does not remove the organisation’s record-keeping obligations.",
      "Records must be provided immediately upon a regulatory request or a valid and enforceable law-enforcement request. The guidance ordinarily expects provision to the regulator within one business day.",
      "Electronic and overseas records must remain accessible. Where secrecy or data-protection legislation restricts access, certified copies must be obtained without delay and retained in a jurisdiction permitting the required access."
    ]
  }
};

export default regulatoryTopicOverviews;

export const riskOverviewAdditions = {
  "risk-factors": {
    "difc": {
      "title": "Additional provisions — DIFC organisations",
      "points": [
        "The DFSA AML Rulebook specifies assessment inputs relating to customers and beneficial owners, the relationship, ownership and control, jurisdictions, products and the business risk assessment.",
        "It provides distinct lists of factors indicating potentially higher and lower risk, covering customer, product and geographic characteristics.",
        "The presence of one or more listed factors does not necessarily establish a high or low risk in a particular situation."
      ]
    },
    "adgm": {
      "title": "Additional provisions — ADGM organisations",
      "points": [
        "The FSRA AML Rulebook specifies business and customer assessment inputs and distinct lists of potentially higher- and lower-risk factors.",
        "Those inputs include ownership and control, jurisdictions, delivery arrangements, products, transactions and the outcomes of the business risk assessment.",
        "Credible sources for jurisdictional assessments include relevant evaluations and reports from FATF, the IMF, the World Bank, the OECD and other international organisations."
      ]
    }
  },
  "risk-rating": {
    "difc": {
      "title": "Additional provisions — DIFC organisations",
      "points": [
        "The DFSA AML Rulebook requires a risk-based assessment of every customer and a rating proportionate to the customer’s money-laundering risks, before CDD for new customers and when appropriate for existing customers.",
        "The DFSA guidance envisages descriptive ratings, such as low, medium or high, or a sliding numeric scale. The resulting assessment determines the appropriate degree of CDD.",
        "Customers with similar profiles may receive different ratings where the relevant product or relationship circumstances justify the distinction."
      ]
    },
    "adgm": {
      "title": "Additional provisions — ADGM organisations",
      "points": [
        "The FSRA AML Rulebook requires a proportionate customer rating before establishing the relationship and refreshment of existing assessments through ongoing CDD.",
        "Its guidance provides for descriptive or ordinal numeric scales with at least three differentiated ratings, linked to simplified, standard or enhanced CDD.",
        "A customer or beneficial owner who is a foreign PEP requires a high-risk rating. Domestic and international-organisation PEP cases require that rating where the relationship is assessed as higher risk after the prescribed assessment and CDD."
      ]
    }
  }
};

export const periodicReviewOverviewAdditions = {
  "difc": {
    "title": "Additional provisions — DIFC organisations",
    "points": [
      "The DFSA AML Rulebook requires reviews of the adequacy of CDD information and the appropriateness of customer risk ratings, both periodically and when a material change or event occurs.",
      "Reviews should address changing identity information, including addresses and corporate ownership records. Triggers include changes in CDD requirements, expected unusual transactions and material changes to the relationship or customer.",
      "The degree of ongoing due diligence depends on the customer risk assessment. Customers, their business and transactions must also be reviewed against the relevant sanctions lists when conducting the CDD information review."
    ]
  },
  "adgm": {
    "title": "Additional provisions — ADGM organisations",
    "points": [
      "The FSRA AML Rulebook requires periodic reviews of CDD information and customer risk ratings, with particular attention to high-risk customers.",
      "Evidence and information should remain current, valid and unexpired, including relevant identification documents, addresses, authorised signatories and ownership records.",
      "Reviews should respond to changes in CDD requirements, expected unusual transactions and material changes in the relationship or customer. The review must also include customers, their businesses and transactions against sanctions lists."
    ]
  }
};

export const sowSofOverviewAdditions = {
  "difc": {
    "title": "Additional provisions — DIFC organisations",
    "points": [
      "The DFSA AML Rulebook distinguishes source of wealth—the acquisition or accumulation of overall wealth—from source of funds—the origin of funds for a transaction or service and their connection to that wealth.",
      "Enhanced due diligence requires reasonable measures to establish both sources, to the extent applicable. Enquiries may need to address the beneficial owner because that person may be the source of the customer’s funds.",
      "Independent corroborating evidence may include dividend records, bank statements, salary or bonus certificates, loan documents and evidence of the underlying transaction. Wealth evidence may include ownership records, probate documents, financial statements and reliable published information.",
      "The Rulebook also requires reasonable measures to establish both sources for PEP customers or beneficial owners and for PEP life-insurance beneficiaries or their beneficial owners before payout."
    ]
  },
  "adgm": {
    "title": "Additional provisions — ADGM organisations",
    "points": [
      "The FSRA AML Rulebook distinguishes the acquisition or accumulation of overall wealth from the origin of funds used for a transaction or service and their connection to that wealth.",
      "Enhanced due diligence is required for high-risk customers, customers from jurisdictions subject to a call for action, and specified transfers involving unhosted wallets. The selected rule requires identification and verification of the source of funds and wealth of the customer and, where applicable, all beneficial owners.",
      "The extent of enhanced measures is determined by their applicability and the risks involved. Enquiries may need to address beneficial owners where the funds originate from them.",
      "Verification includes independent corroborating evidence, such as dividend records, bank statements, salary or bonus certificates, loan and transaction documents, ownership records, probate documents and financial statements. The connection between the funds and the event giving rise to them should be documented."
    ]
  }
};
